import React, {useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'
import {useSsrEffect} from '@modifi/client-ssr'

import {DefaultThemeProvider} from '@modifi/ui'
import {aChangeLocale, KEY_JANUS, useTranslation, extractFromLocale} from '@modifi/plugin-i18n'
import {InferPropTypes} from '@modifi/typescript-utils'
import {compose} from 'ramda'
import {withRouter} from '@modifi/router'
import {connect} from '@modifi/redux'

import SeoHeader from '../common-components/seo-header'
import Footer from './components/footer'

import {GlobalStyles} from '../styles'
import {translationFilter} from '../utils/translation-filter'

import CookieBar from './components/cookie-bar'
import {customTheme} from './customTheme'
import {BasePathProvider} from '../features/base-path'
import HeaderNavigation from './components/header-navigation'
import {RouteInfoContextProvider} from '../features/route-info'
import {isCnTldOrRegion} from './components/footer/components'
import {aFetchNavigationRoutes, selectBasePath} from '../data/navigation'
import {selectAlternativeRoutes} from '../data/routes'
import {aStoreCampaignData} from '../features/sign-up/store'

const propTypes = {
  children: PropTypes.node.isRequired,
  basePath: PropTypes.string.isRequired,
  persistCampaign: PropTypes.func.isRequired,
  skipSeoTags: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  routeDefinition: PropTypes.shape({
    localized: PropTypes.bool,
    limitedLocale: PropTypes.arrayOf(PropTypes.string),
    canonical: PropTypes.string,
  }),
  fetchNavigation: PropTypes.func.isRequired,
  region: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  currentPage: PropTypes.shape({
    pageType: PropTypes.string,
    pageNAme: PropTypes.string,
    component: PropTypes.string,
  }),
  pageAlternatives: PropTypes.objectOf(PropTypes.objectOf(PropTypes.string.isRequired).isRequired),
  changeLocale: PropTypes.func.isRequired,
  disableTopNavigationTransparency: PropTypes.bool,
  seoMeta: PropTypes.shape({
    canonicalLinks: PropTypes.objectOf(PropTypes.string.isRequired),
    hrefLang: PropTypes.objectOf(PropTypes.string.isRequired),
  }),
}

const defaultProps = {
  skipSeoTags: false,
  routeDefinition: undefined,
  currentPage: undefined,
  pageAlternatives: undefined,
  disableTopNavigationTransparency: false,
  seoMeta: undefined,
}

export type LayoutProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const Layout: React.FC<LayoutProps> = ({
  children,
  skipSeoTags,
  persistCampaign,
  fetchNavigation,
  region,
  locale,
  basePath,
  pageAlternatives,
  changeLocale,
  seoMeta,
  disableTopNavigationTransparency,
}) => {
  const {t, i18n} = useTranslation(KEY_JANUS)
  const [, i18nRegion] = extractFromLocale(i18n.language)

  useEffect(() => {
    persistCampaign()
  }, [persistCampaign])

  useSsrEffect(() => {
    fetchNavigation(region, locale)
  }, [fetchNavigation, region, locale])

  useEffect(() => {
    if (locale) {
      // @todo compatibility - use en_GB for now instead of en_US
      if (locale === 'en_US') {
        changeLocale('en_GB')
      } else {
        changeLocale(locale)
      }
    }
  }, [locale, changeLocale])

  const title = translationFilter(t, 'content.fallback.title')

  return (
    <RouteInfoContextProvider
      currentLocale={locale}
      currentRegion={region}
      alternativeRoutes={pageAlternatives}
    >
      <Helmet>
        {title && <title>{title !== 'content.fallback.title' ? title : ''}</title>}
        <meta name="description" content={t('content.fallback.meta-description')} />
        <meta name="keywords" content={t('content.fallback.meta-keywords')} />
        <meta name="robots" content="all" />
        {isCnTldOrRegion(i18nRegion) && <script type="text/javascript" src="/static/baidu.js" />}
      </Helmet>
      {!skipSeoTags && seoMeta && (
        <SeoHeader canonicalLinks={seoMeta.canonicalLinks} hrefLang={seoMeta.hrefLang} />
      )}

      {useMemo(
        () => (
          <DefaultThemeProvider themeExtension={customTheme}>
            <BasePathProvider basePath={basePath}>
              <HeaderNavigation disableTransparency={disableTopNavigationTransparency} />
              {children}
              <Footer />
              <CookieBar />
            </BasePathProvider>
            <GlobalStyles />
          </DefaultThemeProvider>
        ),
        [basePath, children, disableTopNavigationTransparency]
      )}
    </RouteInfoContextProvider>
  )
}

Layout.propTypes = propTypes
Layout.defaultProps = defaultProps

const mapStateToProps = (
  state: Record<string, any>,
  ownProps: Partial<LayoutProps>
): Pick<LayoutProps, 'basePath' | 'pageAlternatives'> => ({
  basePath: selectBasePath(state),
  pageAlternatives: ownProps.currentPage
    ? selectAlternativeRoutes(state, {
        ...ownProps.currentPage,
        region: ownProps.region,
        locale: ownProps.locale,
      })
    : {},
})

const mapDispatchToProps = {
  persistCampaign: aStoreCampaignData,
  fetchNavigation: aFetchNavigationRoutes,
  changeLocale: aChangeLocale,
}

// @ts-expect-error
export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(Layout)
